import { SOCIAL_OG_IMAGE, SOCIAL_OG_IMAGE_ALT } from "@/lib/social-image";
import { BlogHero } from "@/components/BlogHero";
import { blogHeroUrl, blogHeroAlt } from "@/data/_media/blog-hero";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { SITE } from "@/lib/site-config";
import {
  buildJsonLd,
  jsonLdScript,
  webPageSchema,
  breadcrumbListSchema,
  articleSchema,
  faqPageSchema,
} from "@/lib/schema";
import { canonicalAndHreflang } from "@/lib/hreflang";

/** Korean mirror of the corporate-documents-for-abroad guide. */
const TITLE = "태국 회사 서류 해외 사용 — 수리되는 인증 진행법";
const DESC =
  "태국 법인 서류는 어느 단계의 인증이 필요한지, 올바른 절차 순서, 그리고 반송을 막는 제출 전 점검 항목을 정리했습니다.";
const PATH = "/blog/company-documents-for-abroad";
const URL = `/ko${PATH}`;
const PUBLISHED = "2026-08-10";

const FAQ = [
  {
    question: "어떤 회사 서류가 해외에서 자주 요구됩니까?",
    answer:
      "법인등기 증명, 주주명부(บอจ.5), 정관·설립 서류, 감사받은 재무제표, 이사회 결의와 위임장, 그리고 서명자의 신분·직위 증명입니다.",
  },
  {
    question: "인증 사본은 어디에서 발급받습니까?",
    answer:
      "상업개발국(DBD)에서 최근 발급된 인증 사본을 받는 것이 일반적입니다. 제출 기관이 발급 시점을 제한하는 경우가 많으므로 일정에 맞춰 발급하십시오.",
  },
  {
    question: "인증 순서는 어떻게 됩니까?",
    answer:
      "인증 사본 확보 → 필요 시 변호사 공증(서명 확인) → 태국 외교부 영사국 확인 → 목적지국 주태 대사관·영사관 확인 순서입니다.",
  },
  {
    question: "재무제표도 인증할 수 있습니까?",
    answer:
      "가능합니다. 통상 이사 서명과 변호사 확인을 거친 뒤 영사국 확인으로 진행합니다. 감사인 서명본을 요구하는 기관도 있습니다.",
  },
  {
    question: "번역은 어느 언어로 해야 합니까?",
    answer:
      "제출 기관이 수리하는 언어에 맞춥니다. 번역문에는 도장·직인·부기 문구가 모두 포함되어야 합니다.",
  },
  {
    question: "이사가 태국 밖에 있어도 진행할 수 있습니까?",
    answer:
      "가능합니다. 인증된 위임장으로 대리 진행하며, 위임 범위에 서류 신청·수령·인증·제출을 모두 포함해야 합니다.",
  },
  {
    question: "반송의 흔한 사유는 무엇입니까?",
    answer:
      "회사명 표기 불일치, 발급 시점 초과, 권한 연결(결의–위임장–서명자)의 단절, 도장 문구 번역 누락, 그리고 부속 서류·간인 누락입니다.",
  },
];

export const Route = createFileRoute("/ko/blog/company-documents-for-abroad")({
  component: KoCorporateGuide,
  loader: () => ({
    h: {
      meta: [
        { title: ctrTitle(`${TITLE} — ${SITE.name}`) },
        { name: "description", content: ctrDesc(DESC) },
        { property: "og:title", content: ctrTitle(TITLE) },
        { property: "og:description", content: ctrDesc(DESC) },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `https://${SITE.canonicalHost}${URL}` },
        { property: "og:locale", content: "ko" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: blogHeroUrl("company-documents-for-abroad") ?? SOCIAL_OG_IMAGE },
        { property: "og:image:alt", content: blogHeroAlt("company-documents-for-abroad", "ko") ?? SOCIAL_OG_IMAGE_ALT },
        { name: "twitter:image", content: blogHeroUrl("company-documents-for-abroad") ?? SOCIAL_OG_IMAGE },
        {
          name: "keywords",
          content:
            "태국 회사 서류 인증, 태국 법인등기 증명 번역, บอจ.5 번역, 태국 영사국 인증, 이사회 결의 공증",
        },
      ],
      links: canonicalAndHreflang(PATH, null, "ko"),
      scripts: [
        jsonLdScript(
          buildJsonLd([
            webPageSchema({ url: URL, title: TITLE, description: DESC, lastModified: PUBLISHED, speakable: true }),
            breadcrumbListSchema([
              { name: "홈", url: "/ko" },
              { name: "가이드", url: "/blog" },
              { name: TITLE, url: URL },
            ]),
            articleSchema({
            image: blogHeroUrl("company-documents-for-abroad"),
              url: URL,
              headline: TITLE,
              description: DESC,
              datePublished: PUBLISHED,
              dateModified: PUBLISHED,
              articleSection: "Corporate",
            }),
            faqPageSchema(FAQ),
          ]),
        ),
      ],
    },
  }),
  head: ({ loaderData }) => loaderData?.h ?? {},
});

const DOCS: Array<{ doc: string; layer: string }> = [
  { doc: "법인등기 증명(최근 발급)", layer: "인증 사본 → 영사국 → 대사관" },
  { doc: "주주명부 บอจ.5", layer: "인증 사본 → 영사국 → 대사관" },
  { doc: "정관 / 설립 서류", layer: "인증 사본 → 영사국(요건에 따라)" },
  { doc: "감사받은 재무제표", layer: "이사 서명 → 변호사 확인 → 영사국" },
  { doc: "이사회 결의 / 위임장", layer: "변호사 확인 → 영사국 → 대사관" },
  { doc: "서명자 신분증과 직위 증명", layer: "본 서류와 함께 제출" },
];

const STEPS: string[] = [
  "제출 기관의 서면 목록으로 필요 서류와 발급 시점 요건 확인",
  "상업개발국에서 최근 인증 사본 신청",
  "권한 연결 정리: 결의, 위임장, 서명자 신분 서류",
  "서명 확인이 필요한 서류는 공증 변호사 앞에서 처리",
  "태국 외교부 영사국 확인 진행",
  "목적지국 주태 대사관·영사관 확인 후 제출 전 항목별 점검",
];

const CHECKS: string[] = [
  "모든 서류의 회사명 표기가 완전히 일치",
  "발급일이 제출 기관의 시한 이내",
  "위임 범위가 실제 처리할 업무와 항목별로 대응",
  "번역문에 도장·직인·부기 문구 포함",
  "페이지 수, 부속 서류, 간인 완비",
];

function KoCorporateGuide() {
  return (
    <article className="mx-auto max-w-3xl px-4 py-12 prose prose-slate dark:prose-invert">
      <nav className="text-sm mb-6 not-prose">
        <Link to="/ko" className="text-muted-foreground hover:text-foreground">
          홈
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to="/blog" className="text-muted-foreground hover:text-foreground">
          가이드
        </Link>
      </nav>

      <h1>{TITLE}</h1>
      <BlogHero slug="company-documents-for-abroad" locale="ko" />
      <p className="answer-summary speakable-summary">
        태국 회사 서류를 해외에서 사용하려면 상업개발국의 최근 인증 사본을 확보하고, 서명 확인이 필요한 서류는 공증 변호사를 거친 뒤
        태국 외교부 영사국 확인과 목적지국 주태 대사관·영사관 확인을 순서대로 진행합니다.
        회사명 표기 일치와 권한 연결의 완결성이 수리 여부를 좌우합니다.
      </p>

      <h2>1. 서류별 필요한 인증 단계</h2>
      <ul>
        {DOCS.map((d) => (
          <li key={d.doc}>
            <strong>{d.doc}:</strong> {d.layer}
          </li>
        ))}
      </ul>

      <h2>2. 진행 순서</h2>
      <ol>
        {STEPS.map((s) => (
          <li key={s}>{s}</li>
        ))}
      </ol>

      <h2>3. 제출 전 점검 항목</h2>
      <ul>
        {CHECKS.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ul>

      <h2>4. 자주 묻는 질문</h2>
      <dl>
        {FAQ.map((f) => (
          <div key={f.question}>
            <dt>
              <strong>{f.question}</strong>
            </dt>
            <dd>{f.answer}</dd>
          </div>
        ))}
      </dl>

      <h2>5. 참고 자료와 관련 서비스</h2>
      <p className="text-sm">
        참고:{" "}
        <a href="https://www.dbd.go.th/" target="_blank" rel="noreferrer">
          태국 상업개발국(DBD)
        </a>{" "}
        ·{" "}
        <a href="https://consular.mfa.go.th/en/publicservice/legalization" target="_blank" rel="noreferrer">
          태국 외교부 영사국
        </a>
      </p>
      <ul className="not-prose mt-4 grid gap-2 text-sm">
        <li>
          <Link to="/ko/corporate" className="text-primary underline">
            회사·비즈니스 법률 서류 서비스
          </Link>
        </li>
        <li>
          <Link to="/ko/lgz" className="text-primary underline">
            서류 인증·영사확인 서비스
          </Link>
        </li>
        <li>
          <Link to="/ko/notary" className="text-primary underline">
            공증 업무 변호사(Notary Public) 서비스
          </Link>
        </li>
      </ul>
      <p className="text-sm text-muted-foreground">
        내용 검토일 {PUBLISHED}. 본 가이드는 일반 정보이며 개별 사안에 대한 법률 의견이 아닙니다. 진행 전 제출 기관의 최신 요건을 확인하십시오.
      </p>
    </article>
  );
}
